import React from "react";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { Badge, BadgeType } from "../types";

interface BadgeDisplayProps {
  badges?: Badge[];
  size?: "small" | "medium" | "large";
}

const BADGE_INFO: Record<
  BadgeType,
  { name: string; description: string; emoji: string; color: string }
> = {
  first_win: {
    name: "First Victory",
    description: "Won your first match",
    emoji: "🎉",
    color: "#4CAF50",
  },
  giant_killer: {
    name: "Giant Killer",
    description: "Defeated a player ranked 5+ positions higher",
    emoji: "⚔️",
    color: "#F44336",
  },
  win_streak_10: {
    name: "Unstoppable",
    description: "Won 10 matches in a row",
    emoji: "🔥",
    color: "#FF5722",
  },
  division_champion: {
    name: "Division Champion",
    description: "Finished #1 in your division",
    emoji: "👑",
    color: "#FFD700",
  },
  close_call: {
    name: "Close Call",
    description: "Won a match by a narrow margin (4-3 or 5-4)",
    emoji: "💪",
    color: "#9C27B0",
  },
  perfect_day: {
    name: "Perfect Day",
    description: "Won all 4 matches in a league day",
    emoji: "⭐",
    color: "#FFC107",
  },
  veteran: {
    name: "Veteran",
    description: "Played 50+ league days",
    emoji: "🏅",
    color: "#607D8B",
  },
  partner_carry: {
    name: "Partner Carry",
    description: "Won with a partner ranked 10+ positions lower",
    emoji: "🎯",
    color: "#00BCD4",
  },
  clean_sweep: {
    name: "Clean Sweep",
    description: "Won a match 5-0 or 6-0",
    emoji: "🧹",
    color: "#8BC34A",
  },
  mr_ms_consistent: {
    name: "Mr/Ms Consistent",
    description: "Won exactly 2 games in 5 consecutive matches",
    emoji: "🎲",
    color: "#FF9800",
  },
  century: {
    name: "Century",
    description: "Won 100+ games in a season with positive game difference",
    emoji: "💯",
    color: "#E91E63",
  },
  iron_man: {
    name: "Iron Man",
    description: "Played 10 consecutive league days",
    emoji: "🦾",
    color: "#FF6B35",
  },
  all_season_warrior: {
    name: "All-Season Warrior",
    description: "Played every league day in a season",
    emoji: "⚔️",
    color: "#673AB7",
  },
  division_hopper: {
    name: "Division Hopper",
    description: "Played in 3+ different divisions",
    emoji: "🔀",
    color: "#009688",
  },
  unlucky: {
    name: "Unlucky",
    description: "Lost 5 matches by close margins (3-4 or 4-5)",
    emoji: "😅",
    color: "#795548",
  },
  sp_team: {
    name: "SP Team",
    description: "Stuttgart Padel League family & friends",
    emoji: "🏆",
    color: "#1976D2",
  },
};

function BadgeDisplay({ badges = [], size = "medium" }: BadgeDisplayProps) {
  if (!badges || badges.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No badges yet
      </Typography>
    );
  }

  const fontSize =
    size === "small" ? "1.2rem" : size === "large" ? "2rem" : "1.5rem";

  return (
    <Box display="flex" flexWrap="wrap" gap={0.5}>
      {badges.map((badge, index) => {
        const info = BADGE_INFO[badge.type];
        if (!info) return null;

        let tooltipContent = `${info.name}: ${info.description}`;

        // Add metadata to tooltip
        if (badge.metadata) {
          if (badge.metadata.opponentName) {
            tooltipContent += ` (Beat ${badge.metadata.opponentName}, ranked ${badge.metadata.rankDifference}+ positions higher)`;
          }
          if (badge.metadata.partnerName) {
            tooltipContent += ` (Partner: ${badge.metadata.partnerName}, ranked ${badge.metadata.rankDifference} positions lower)`;
          }
          if (badge.metadata.streakLength) {
            tooltipContent += ` (${badge.metadata.streakLength} game streak)`;
          }
          if (badge.metadata.totalDays) {
            tooltipContent += ` (${badge.metadata.totalDays} league days)`;
          }
          if (badge.metadata.totalGames) {
            tooltipContent += ` (${badge.metadata.totalGames} games won)`;
          }
          if (badge.metadata.consecutiveMatches) {
            tooltipContent += ` (${badge.metadata.consecutiveMatches} consecutive matches)`;
          }
          if (badge.metadata.consecutiveDays) {
            tooltipContent += ` (${badge.metadata.consecutiveDays} consecutive league days)`;
          }
          if (badge.metadata.divisions) {
            tooltipContent += ` (Divisions: ${badge.metadata.divisions.join(', ')})`;
          }
          if (badge.metadata.closeLosses) {
            tooltipContent += ` (${badge.metadata.closeLosses} close losses)`;
          }
        }

        // Format earned date - handle Firestore Timestamp format
        let earnedDateStr = "Unknown";
        if (badge.earnedAt) {
          try {
            // Check if it's a Firestore Timestamp object with _seconds
            if (
              typeof badge.earnedAt === "object" &&
              "_seconds" in badge.earnedAt
            ) {
              earnedDateStr = new Date(
                (badge.earnedAt as any)._seconds * 1000,
              ).toLocaleDateString("en-GB");
            } else if (
              typeof badge.earnedAt === "object" &&
              "toDate" in badge.earnedAt
            ) {
              // Handle Firestore Timestamp with toDate method
              earnedDateStr = (badge.earnedAt as any)
                .toDate()
                .toLocaleDateString("en-GB");
            } else {
              // Handle regular date string or Date object
              earnedDateStr = new Date(
                badge.earnedAt as any,
              ).toLocaleDateString("en-GB");
            }
          } catch (error) {
            console.error(
              "Error formatting badge earnedAt:",
              error,
              badge.earnedAt,
            );
            earnedDateStr = "Unknown";
          }
        }
        tooltipContent += ` - Earned ${earnedDateStr}`;

        return (
          <Tooltip
            key={`${badge.type}-${index}`}
            title={tooltipContent}
            arrow
            enterTouchDelay={0}
            leaveTouchDelay={3000}
          >
            <Chip
              label={info.emoji}
              size={size === "small" ? "small" : "medium"}
              sx={{
                backgroundColor: info.color,
                color: "white",
                fontWeight: "bold",
                fontSize,
                cursor: "help",
                "&:hover": {
                  transform: "scale(1.1)",
                  transition: "transform 0.2s",
                },
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
}

export default BadgeDisplay;
